import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Paper,
  Grid,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { maintenanceAPI } from "../../utils/api";

const MaintenanceRequestForm = ({ onSuccess }) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [categories] = useState([
    { id: "electrical", name: "Electrical" },
    { id: "plumbing", name: "Plumbing" },
    { id: "hvac", name: "HVAC" },
    { id: "cleaning", name: "Cleaning" },
    { id: "it", name: "IT Support" },
    { id: "general", name: "General Maintenance" },
    { id: "safety", name: "Safety & Security" },
    { id: "furniture", name: "Furniture & Fixtures" },
  ]);

  const [form, setForm] = useState({
    category: "",
    location: "",
    description: "",
    priority: "medium",
    contactPhone: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      // Validate form
      if (!form.category || !form.location || !form.description) {
        setError("Please fill in all required fields.");
        return;
      }

      // Create maintenance request
      const requestData = {
        category: form.category,
        location: form.location,
        description: form.description,
        priority: form.priority,
        contactPhone: form.contactPhone || userProfile?.phone || "",
      };

      // Submit the request
      const response = await maintenanceAPI.createRequest(requestData);
      console.log("Maintenance request created:", response.data);

      setSuccess(
        `Maintenance request submitted successfully! Token: ${response.data.tokenNumber}`
      );
      setForm({
        category: "",
        location: "",
        description: "",
        priority: "medium",
        contactPhone: "",
      });

      // Call success callback to refresh parent stats
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Failed to submit request.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Create Maintenance Request
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Submit a maintenance request for your location. Our team will review and
        assign it to the appropriate department.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={form.category}
                onChange={handleChange}
                label="Category"
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Priority</InputLabel>
              <Select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              name="location"
              label="Location"
              value={form.location}
              onChange={handleChange}
              placeholder="e.g., Hostel Room 12, Lab Building 3rd Floor"
              helperText="Provide specific location details for the maintenance issue"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={4}
              name="description"
              label="Description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the maintenance issue in detail..."
              helperText="Provide clear details about what needs to be fixed or maintained"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="contactPhone"
              label="Contact Phone (Optional)"
              value={form.contactPhone}
              onChange={handleChange}
              placeholder="Your phone number for urgent contact"
              helperText="Leave blank to use your profile phone number"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={<SendIcon />}
              sx={{ mt: 2 }}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </Paper>
  );
};

export default MaintenanceRequestForm;
